// React
import React, { useState, useEffect } from 'react';

// Packages
import t from 'prop-types';
import { useForm, useWatch } from 'react-hook-form';
import { sortTags } from 'utilities';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

// Utilities
import { useLabels, useSearchAndFilter } from 'utilities/hooks';
import { CONSTANTS } from 'utilities/configuration';

// Components
import {
    SearchFilterDate,
    SearchFilterMultiselect,
    SearchFilterSearchMultiselect,
} from 'components/_inputs';

const SearchAndFilter = ({ data, instanceName, filters, allTags }) => {
    // ///////////////////
    // STORES
    // ///////////////////

    const {
        initial,
        setInitial,
        setFiltered,
        filter,
        setFilter,
        setSelected,
    } = useSearchAndFilter({ instanceName });

    // ///////////////////
    // HOOKS
    // ///////////////////

    const { label, getValueLabel, tagLabel } = useLabels();

    // ///////////////////
    // STATE
    // ///////////////////

    const [funderOptions, setFunderOptions] = useState([]);
    const [tagOptions, setTagOptions] = useState([]);
    const [tagCollectionOptions, setTagCollectionOptions] = useState([]);
    const [deadlineOptions, setDeadlineOptions] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [reportStatusOptions, setReportStatus] = useState([]);
    const [reportTypeOptions, setReportTypeOptions] = useState([]);
    const [initiativeTypeOptions, setInitiativeTypeOptions] = useState([]);

    // ///////////////////
    // FORMS
    // ///////////////////

    const { control, register, getValues, setValue } = useForm({
        mode: 'onChange',
    });
    const filterText = useWatch({
        control,
        name: 'filter.text',
        defaultValue: '',
    });
    const filterCategory = useWatch({
        control,
        name: 'filter.category',
        defaultValue: [],
    });
    const filterReportStatus = useWatch({
        control,
        name: 'filter.reportStatus',
        defaultValue: [],
    });
    const filterReportType = useWatch({
        control,
        name: 'filter.reportType',
        defaultValue: [],
    });
    const filterFunder = useWatch({
        control,
        name: 'filter.funder',
        defaultValue: [],
    });
    const filterStartDate = useWatch({
        control,
        name: 'filter.startDate',
    });
    const filterEndDate = useWatch({
        control,
        name: 'filter.endDate',
    });
    const filterDeadline = useWatch({
        control,
        name: 'filter.deadline',
        defaultValue: [],
    });
    const filterTags = useWatch({
        control,
        name: 'filter.tags',
        defaultValue: [],
    });
    const filterTagCollections = useWatch({
        control,
        name: 'filter.tagCollections',
        defaultValue: [],
    });
    const filterInitiativeType = useWatch({
        control,
        name: 'filter.initiativeType',
        defaultValue: [],
    });

    // ///////////////////
    // METHODS
    // ///////////////////

    function onFilter() {
        if (initial && filter) {
            const {
                text,
                category = [],
                reportStatus = [],
                reportType = [],
                funder = [],
                deadline = [],
                tags = [],
                startDate = null,
                endDate = null,
                tagCollections = [],
                initiativeType = [],
            } = filter;

            let nextFiltered;
            const flattenedTags = tags.flat();
            const flattenedTagCollections = tagCollections.flat();

            // Optional text
            nextFiltered = text
                ? initial.filter(item =>
                      item?.__filtering?.text?.some(x =>
                          x?.toLowerCase().includes(text.toLowerCase())
                      )
                  )
                : initial;

            // Optional category
            nextFiltered =
                filters.category && category?.length > 0
                    ? nextFiltered.filter(item =>
                          item?.__filtering?.category?.some(x =>
                              category.includes(x)
                          )
                      )
                    : nextFiltered;

            // Optional reportStatus
            nextFiltered =
                filters.reportStatus && reportStatus?.length > 0
                    ? nextFiltered.filter(item =>
                          item?.__filtering?.reportStatus?.some(x =>
                              reportStatus.includes(x)
                          )
                      )
                    : nextFiltered;

            // Optional reportType
            nextFiltered =
                filters.reportType && reportType?.length > 0
                    ? nextFiltered.filter(item =>
                          item?.__filtering?.reportType?.some(x =>
                              reportType.includes(x)
                          )
                      )
                    : nextFiltered;

            // Optional funder
            nextFiltered =
                filters.funder && funder?.length > 0
                    ? nextFiltered.filter(item =>
                          item?.__filtering?.funder?.some(x =>
                              funder.includes(x.value)
                          )
                      )
                    : nextFiltered;

            // Optional deadline
            nextFiltered =
                filters.deadline && deadline?.length > 0
                    ? nextFiltered.filter(item =>
                          item?.__filtering?.deadline?.some(x =>
                              deadline.includes(x)
                          )
                      )
                    : nextFiltered;

            // Optional start date
            nextFiltered =
                filters.startDate && startDate
                    ? nextFiltered.filter(item => {
                          // Check if grantStart happens AFTER filterStart
                          const dateList = item?.__filtering?.startDate.map(x =>
                              dayjs(x)
                          );
                          const filterStart = dayjs(startDate).format(
                              'YYYY-MM-DD'
                          );
                          return dateList.some(date =>
                              date.isSameOrAfter(filterStart, 'day')
                          );
                      })
                    : nextFiltered;

            // Optional end date
            nextFiltered =
                filters.endDate && endDate
                    ? nextFiltered.filter(item => {
                          // Check if grantEnd happens BEFORE filterEnd
                          const dateList = item?.__filtering?.endDate.map(x =>
                              dayjs(x)
                          );
                          const filterEnd = dayjs(endDate).format('YYYY-MM-DD');
                          return dateList.some(date =>
                              date.isSameOrBefore(filterEnd, 'day')
                          );
                      })
                    : nextFiltered;

            // Optional no tags
            nextFiltered =
                filters.tag && flattenedTags.includes('NO_TAGS')
                    ? nextFiltered.filter(
                          item => item?.__filtering?.tag?.length === 0
                      )
                    : nextFiltered;

            // Optional tags
            nextFiltered =
                filters.tag &&
                flattenedTags.length > 0 &&
                !flattenedTags.includes('NO_TAGS')
                    ? nextFiltered.filter(item =>
                          item?.__filtering?.tag?.some(x =>
                              flattenedTags.includes(x)
                          )
                      )
                    : nextFiltered;

            // Optional tag collections
            nextFiltered =
                filters.tagCollection &&
                flattenedTagCollections.length > 0 &&
                !flattenedTagCollections.includes('NO_TAGS')
                    ? nextFiltered.filter(item =>
                          item?.__filtering?.tagCollection?.some(x =>
                              flattenedTagCollections.includes(x)
                          )
                      )
                    : nextFiltered;

            // Optional initiative type
            nextFiltered =
                filters.initiativeType && initiativeType?.length > 0
                    ? nextFiltered.filter(item =>
                          item?.__filtering?.initiativeType?.some(x =>
                              initiativeType.includes(x)
                          )
                      )
                    : nextFiltered;

            // Set filtered and selected

            setFiltered(nextFiltered);
            setSelected(nextFiltered);
        }
    }

    // ///////////////////
    // EFFECTS
    // ///////////////////

    useEffect(() => {
        setInitial(data);
        setFiltered(data);
        setSelected(data);
    }, []);

    // Set initial filter
    useEffect(() => {
        if (filter) {
            Object.keys(filter).forEach(key => {
                setValue(`filter.${key}`, filter[key]);
            });
        }
    }, []);

    // Set deadline options
    useEffect(() => {
        if (!initial || !filters.deadline) return;

        // Set deadlineOptions
        const deadlineOptions = [
            ...new Set(
                initial
                    .map(item => item.__filtering.deadline.map(x => x))
                    .flat()
            ),
        ].sort((a, b) => a.localeCompare(b));

        setDeadlineOptions(deadlineOptions);
    }, [initial]);

    // Set funder options
    useEffect(() => {
        if (!initial || !filters.funder) return;

        // Remove duplicates and set
        setFunderOptions(
            [
                ...new Set(
                    initial
                        .map(item => item.__filtering.funder.map(x => x))
                        .flat()
                        .map(JSON.stringify)
                ),
            ]
                .map(JSON.parse)
                .sort((a, b) => a.label.localeCompare(b.label))
        );
    }, [initial]);

    // Set funder tags
    useEffect(() => {
        if (!initial || !filters.tag) return;

        // Make array of tags with the format { value: [tagIds], label: 'tagLabel'}
        // If a tag label exists more than one, the value should be added to the array of tagIds
        const tagIds = {};

        // Sort tags and set tag options
        sortTags(allTags)
            .filter(tag => tag.Type__c === CONSTANTS.TAGS.INITIATIVE)
            .forEach(tag => {
                // Set tag name
                const tagName = tagLabel(tag);
                const tagId = tag.Id;
                if (tagIds[tagName]) {
                    if (!tagIds[tagName].includes(tagId)) {
                        tagIds[tagName].push(tagId);
                    }
                } else {
                    tagIds[tagName] = [tagId];
                }
            });

        // Remove duplicates and set
        setTagOptions([
            { value: 'NO_TAGS', label: label('OverviewNoTagsSet') },
            ...Object.entries(tagIds).map(([key, value]) => ({
                value: value,
                label: key,
            })),
        ]);
    }, [initial]);

    // Set tag collections
    useEffect(() => {
        if (!initial || !filters.tag) return;

        // Make array of tags with the format { value: [tagIds], label: 'tagLabel'}
        // If a tag label exists more than one, the value should be added to the array of tagIds
        const tagIds = {};

        // Sort tags and set tag options
        sortTags(allTags)
            .filter(tag => tag.Type__c === CONSTANTS.TAGS.COLLECTION)
            .forEach(tag => {
                // Set tag name
                const tagName = tagLabel(tag);
                const tagId = tag.Id;
                if (tagIds[tagName]) {
                    if (!tagIds[tagName].includes(tagId)) {
                        tagIds[tagName].push(tagId);
                    }
                } else {
                    tagIds[tagName] = [tagId];
                }
            });

        // Remove duplicates and set
        setTagCollectionOptions([
            { value: 'NO_TAGS', label: label('OverviewNoTagsSet') },
            ...Object.entries(tagIds).map(([key, value]) => ({
                value: value,
                label: key,
            })),
        ]);
    }, [initial]);

    // Set category options
    useEffect(() => {
        if (!initial || !filters.category) return;

        // Set category options
        const categoryOptions = [
            ...new Set(
                initial
                    .map(item => item.__filtering.category.map(x => x))
                    .flat()
            ),
        ]
            .map(x => ({
                label: getValueLabel('Initiative__c.Category__c', x),
                value: x,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));

        setCategoryOptions(categoryOptions);
    }, [initial]);

    // Set reportStatus options
    useEffect(() => {
        if (!initial || !filters.reportStatus) return;

        // Set status options
        const reportStatusOptions = [
            ...new Set(
                initial
                    .map(item => item.__filtering.reportStatus.map(x => x))
                    .flat()
            ),
        ]
            .map(x => ({
                label: getValueLabel('Initiative_Report__c.Status__c', x),
                value: x,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));

        setReportStatus(reportStatusOptions);
    }, [initial]);

    // Set reportType options
    useEffect(() => {
        if (!initial || !filters.reportType) return;

        // Set type options
        const reportTypeOptions = [
            ...new Set(
                initial
                    .map(item => item.__filtering.reportType.map(x => x))
                    .flat()
            ),
        ]
            .map(x => ({
                label: getValueLabel('Initiative_Report__c.Report_Type__c', x),
                value: x,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));

        setReportTypeOptions(reportTypeOptions);
    }, [initial]);

    // Set initiative type options
    useEffect(() => {
        if (!initial || !filters.initiativeType) return;

        // Set initiative type options
        const initiativeTypeOptions = [
            ...new Set(
                initial
                    .map(item => item.__filtering.initiativeType.map(x => x))
                    .flat()
            ),
        ]
            .filter(x => x !== 'Default' && x !== 'Reporting')
            .map(x => ({
                label: getValueLabel('Initiative__c.Configuration_Type__c', x),
                value: x,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));

        setInitiativeTypeOptions(initiativeTypeOptions);
    }, [initial]);

    useEffect(() => {
        setFilter(getValues()?.filter);
    }, [
        filterCategory,
        filterText,
        filterReportStatus,
        filterReportType,
        filterFunder,
        filterStartDate,
        filterEndDate,
        filterDeadline,
        filterTags,
        filterTagCollections,
        filterInitiativeType,
    ]);

    useEffect(() => {
        onFilter();
    }, [filter]);

    // ///////////////////
    // RENDER
    // ///////////////////

    return (
        <div className="flex flex-col">
            <input
                {...register('filter.text')}
                type="text"
                placeholder={label('InitiativeManagerSearchBoxText')}
                className="input-search"
            />
            <div className="flex flex-wrap mt-16 -m-8">
                {filters?.funder && (
                    <SearchFilterSearchMultiselect
                        name="filter.funder"
                        label={label('ReportManagerFilterFoundation')}
                        controller={control}
                        options={funderOptions}
                    />
                )}
                {filters?.tagCollection && (
                    <SearchFilterSearchMultiselect
                        name="filter.tagCollections"
                        label={label(
                            'InitiativeManagerFilterFilterTagCollections'
                        )}
                        controller={control}
                        options={tagCollectionOptions}
                    />
                )}
                {filters?.tag && (
                    <SearchFilterSearchMultiselect
                        name="filter.tags"
                        label={label('InitiativeManagerFilterFilterTags')}
                        controller={control}
                        options={tagOptions}
                    />
                )}
                {filters?.category && (
                    <SearchFilterMultiselect
                        name="filter.category"
                        label={label('ReportManagerFilterGrantGivingArea')}
                        controller={control}
                        options={categoryOptions}
                    />
                )}
                {filters?.initiativeType && (
                    <SearchFilterMultiselect
                        name="filter.initiativeType"
                        label={label('ReportManagerFilterInitiativeType')}
                        controller={control}
                        options={initiativeTypeOptions}
                    />
                )}
                {filters?.reportStatus && (
                    <SearchFilterMultiselect
                        name="filter.reportStatus"
                        label={label('ReportManagerFilterReportStatus')}
                        controller={control}
                        options={reportStatusOptions}
                    />
                )}
                {filters?.reportType && (
                    <SearchFilterMultiselect
                        name="filter.reportType"
                        label={label('ReportManagerFilterReportType')}
                        controller={control}
                        options={reportTypeOptions}
                    />
                )}
                {filters?.startDate && (
                    <SearchFilterDate
                        name="filter.startDate"
                        label={label('InitiativeManagerFilterGrantStartDate')}
                        controller={control}
                    />
                )}
                {filters?.endDate && (
                    <SearchFilterDate
                        name="filter.endDate"
                        label={label('InitiativeManagerFilterGrantEndDate')}
                        controller={control}
                    />
                )}
                {filters?.deadline && (
                    <SearchFilterMultiselect
                        name="filter.deadline"
                        label={label('ReportManagerFilterYear')}
                        controller={control}
                        options={deadlineOptions.map(x => ({
                            label: x,
                            value: x,
                        }))}
                    />
                )}
            </div>
        </div>
    );
};

SearchAndFilter.propTypes = {
    instanceName: t.string.isRequired,
    data: t.arrayOf(
        t.shape({
            __filtering: t.shape({
                category: t.arrayOf(t.string),
                deadline: t.arrayOf(t.string),
                endDate: t.arrayOf(t.string),
                funder: t.arrayOf(
                    t.shape({ label: t.string, value: t.string })
                ),
                reportStatus: t.arrayOf(t.string),
                reportType: t.arrayOf(t.string),
                startDate: t.arrayOf(t.string),
                tag: t.arrayOf(t.string),
                text: t.arrayOf(t.string),
                initiativeType: t.arrayOf(t.string),
            }),
        })
    ),
    filters: t.shape({
        category: t.bool,
        deadline: t.bool,
        endDate: t.bool,
        funder: t.bool,
        reportStatus: t.bool,
        reportType: t.bool,
        startDate: t.bool,
        tags: t.bool,
        tagCollection: t.bool,
        initiativeType: t.bool,
    }),
};

SearchAndFilter.defaultProps = {
    data: [],
    filters: {
        category: false,
        deadline: false,
        endDate: false,
        funder: false,
        reportStatus: false,
        reportType: false,
        startDate: false,
        tag: false,
        tagCollection: false,
        initiativeType: false,
    },
    instanceName: '',
};

export default SearchAndFilter;
